/**
 * @file AdditionalUserForm.js
 * @description Form component for creating and editing additional users.
 */

import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import styles from './AdditionalUsers.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Tooltip from '../ui/tooltips/Tooltip';
import { Dropdown } from '../helpers/Dropdown';
import {
    getAdditionalUser,
    createAdditionalUser,
    updateAdditionalUser,
    deleteAdditionalUser,
} from '../../services/userService';
import { initiateAdditionalUserOnboarding } from '../../services/individualService';
import { relationshipMap, SecurityAttributes } from '../../utils/data/enums';
import { isValidEmailAddress } from '../../utils/validate';
import { format } from 'date-fns';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES } from '../../constants/routes';
import { useIndividualHasBenefitAccess } from '../../hooks/useIndividualHasBenefitAccess';
import { useAuthContext } from '../../store/Contexts';
import { TenantTiers } from '../../utils/data/enums';
import { getText } from '../../i18n';

const BackNavIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
        viewBox="0 0 30 30"
        fill="none"
    >
        <g clipPath="url(#clip0_auf_back)">
            <path
                d="M12.5 27.5L0 15L12.5 2.5L14.7188 4.71875L4.4375 15L14.7188 25.2813L12.5 27.5Z"
                fill="#2F2B43"
            />
        </g>
        <defs>
            <clipPath id="clip0_auf_back">
                <rect width="30" height="30" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const AdditionalUserForm = ({
    userId,
    onClose,
    onSuccess,
    isModal = false,
}) => {
    const { showModal } = useContext(ModalContext);
    const { userId: routeUserId } = useParams();
    const navigate = useNavigate();
    const resolvedUserId = userId || routeUserId;
    const isEditMode = !!resolvedUserId;
    const isMobilePage = !isModal && window.innerWidth < 576;
    const { clientTierMeetsRequirement } = useAuthContext();

    const goBack = () => {
        if (onClose) {
            onClose();
        } else {
            navigate(ROUTES.ADDITIONAL_USERS);
        }
    };

    const [isLoading, setIsLoading] = useState(isEditMode);
    const [isSaving, setIsSaving] = useState(false);
    const [isSendingInvite, setIsSendingInvite] = useState(false);

    const [formData, setFormData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        email_address: '',
        relationship_id: '',
        access_benefits: false,
        access_portals: false,
        access_private_assets: false,
        access_sso: false,
    });

    const [originalFormData, setOriginalFormData] = useState(null);
    const [invitationExpiresAt, setInvitationExpiresAt] = useState(null);

    const [errors, setErrors] = useState({
        first_name: '',
        last_name: '',
        email_address: '',
        relationship_id: '',
        access: '',
    });

    const relationshipOptions = [
        { label: getText('msg_b3a58723dc61'), value: '' },
        ...Object.entries(relationshipMap).map(([id, name]) => ({
            label: name,
            value: id,
        })),
    ];

    const showIndividualBenefits = useIndividualHasBenefitAccess();

    useEffect(() => {
        if (isEditMode) {
            loadUserData();
        }
    }, [resolvedUserId, isEditMode]);

    const mapSecurityAttributesToAccess = security_attributes => {
        if (!security_attributes || !Array.isArray(security_attributes)) {
            return {
                access_benefits: false,
                access_portals: false,
                access_private_assets: false,
                access_sso: false,
            };
        }

        return {
            access_benefits: security_attributes.includes(
                SecurityAttributes.UserAccessBenefits,
            ),
            access_portals: security_attributes.includes(
                SecurityAttributes.UserAccessPortals,
            ),
            access_private_assets: security_attributes.includes(
                SecurityAttributes.UserAccessPrivateAssets,
            ),
            access_sso: security_attributes.includes(
                SecurityAttributes.UserAccessSSOs,
            ),
        };
    };

    const mapAccessToSecurityAttributes = () => {
        const attributes = [];

        if (formData.access_benefits) {
            attributes.push(SecurityAttributes.UserAccessBenefits);
        }
        if (formData.access_portals) {
            attributes.push(SecurityAttributes.UserAccessPortals);
        }
        if (formData.access_private_assets) {
            attributes.push(SecurityAttributes.UserAccessPrivateAssets);
        }
        if (formData.access_sso) {
            attributes.push(SecurityAttributes.UserAccessSSOs);
        }

        return attributes;
    };

    const loadUserData = async () => {
        try {
            setIsLoading(true);
            const userData = await getAdditionalUser(resolvedUserId);

            const accessPermissions = mapSecurityAttributesToAccess(
                userData.security_attributes,
            );

            const mappedData = {
                first_name: userData.first_name || '',
                middle_name: userData.middle_name || '',
                last_name: userData.last_name || '',
                email_address: userData.email_address || '',
                relationship_id: userData.relationship_id
                    ? String(userData.relationship_id)
                    : '',
                ...accessPermissions,
            };

            setFormData(mappedData);
            setOriginalFormData(JSON.parse(JSON.stringify(mappedData)));
            setInvitationExpiresAt(userData.invitation_expires_at || null);
        } catch (error) {
            console.error('Failed to load user data', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_d8886a8e995a'));
            }
            if (onClose) {
                onClose();
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = e => {
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({
            ...prev,
            [name]: fieldValue,
        }));

        setErrors(prev => ({
            ...prev,
            [name]: '',
            access: '',
        }));
    };

    const handleDropdownChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value === '' ? '' : value,
        }));

        setErrors(prev => ({
            ...prev,
            [fieldName]: '',
        }));
    };

    const validateForm = () => {
        const newErrors = {
            first_name: '',
            last_name: '',
            email_address: '',
            relationship_id: '',
            access: '',
        };

        let isValid = true;

        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
            isValid = false;
        }

        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
            isValid = false;
        }

        if (!formData.email_address.trim()) {
            newErrors.email_address = 'Email is required';
            isValid = false;
        } else if (!isValidEmailAddress(formData.email_address)) {
            newErrors.email_address = 'Please enter a valid email address';
            isValid = false;
        }

        if (!formData.relationship_id) {
            newErrors.relationship_id = 'Relationship is required';
            isValid = false;
        }

        if (!formData.access_benefits && !formData.access_portals) {
            newErrors.access =
                'User must have access to benefits or portals in order to be saved';
            isValid = false;
        }

        setErrors(newErrors);

        if (!isValid) {
            const errorMessages = Object.values(newErrors).filter(
                msg => msg !== '',
            );
            if (errorMessages.length === 1) {
                Toast.error(errorMessages[0]);
            } else {
                Toast.error(getText('msg_f404e029452e'));
            }
        }

        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setIsSaving(true);

            const payload = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                email_address: formData.email_address,
                relationship_id: Number(formData.relationship_id),
                security_attributes: mapAccessToSecurityAttributes(),
            };

            if (formData.middle_name && formData.middle_name.trim()) {
                payload.middle_name = formData.middle_name;
            }

            if (isEditMode) {
                await updateAdditionalUser(resolvedUserId, payload);
                Toast.success(getText('msg_1f3f78b01f87'));
                if (onSuccess) {
                    onSuccess();
                }
                goBack();
            } else {
                await createAdditionalUser(payload);
                Toast.success(getText('msg_e3bff57687ca'));
                if (onSuccess) {
                    onSuccess();
                }
                goBack();
            }
        } catch (error) {
            console.error('Failed to save additional user', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_f8ba3cce7d65'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleSendInvite = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setIsSendingInvite(true);

            const payload = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                email_address: formData.email_address,
                relationship_id: Number(formData.relationship_id),
                security_attributes: mapAccessToSecurityAttributes(),
            };

            if (formData.middle_name && formData.middle_name.trim()) {
                payload.middle_name = formData.middle_name;
            }

            if (isEditMode) {
                await updateAdditionalUser(resolvedUserId, payload);

                try {
                    await initiateAdditionalUserOnboarding(
                        resolvedUserId,
                        formData.email_address,
                        null,
                    );
                    Toast.success(getText('msg_dc475d7414ab'));
                    await loadUserData();
                } catch (error) {
                    console.error('Failed to initiate onboarding:', error);
                    if (error?.response?.data?.detail) {
                        Toast.error(error.response.data.detail);
                    } else {
                        Toast.error(getText('msg_67566fbb0865'));
                    }
                }
            } else {
                const created = await createAdditionalUser(payload);

                const createdId =
                    created?.id ||
                    created?.user_id ||
                    created?.tenantUserID ||
                    null;

                if (!createdId) {
                    Toast.success(getText('msg_e3bff57687ca'));
                    if (onSuccess) {
                        onSuccess();
                    }
                    goBack();
                    return;
                }

                try {
                    const savedUser = await getAdditionalUser(createdId);
                    const savedEmail =
                        savedUser?.email_address || formData.email_address;
                    const savedMobile = savedUser?.mobile || null;
                    await initiateAdditionalUserOnboarding(
                        createdId,
                        savedEmail,
                        savedMobile,
                    );
                    Toast.success(getText('msg_b60e65ad23fd'));
                    if (onSuccess) {
                        onSuccess();
                    }
                    goBack();
                } catch (error) {
                    console.error(
                        'Failed to initiate onboarding after create:',
                        error,
                    );
                    if (error?.response?.data?.detail) {
                        Toast.error(error.response.data.detail);
                    } else {
                        Toast.error(getText('msg_67566fbb0865'));
                    }
                    if (onSuccess) {
                        onSuccess();
                    }
                    goBack();
                }
            }
        } catch (error) {
            console.error('Failed to send invitation', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_67566fbb0865'));
            }
        } finally {
            setIsSendingInvite(false);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteAdditionalUser(resolvedUserId);
            Toast.success(getText('msg_1af5d61ae4c4'));
            if (onSuccess) {
                onSuccess();
            }
            goBack();
        } catch (error) {
            console.error('Failed to delete additional user', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_5ddbd92b17c2'));
            }
        }
    };

    const openDeleteConfirmationModal = () => {
        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_b957efeb6067'),
            subtitle: getText('msg_1f6d43ba0360'),
            text: getText('msg_a8c1dd9344f8'),
            onYes: () => handleDelete(),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(LogEventType.DeleteClicked, 'Additional user delete clicked');
    };

    const handleClose = () => {
        if (checkUnsavedChanges()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () => goBack(),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_36fff63ccbcd'),
            });
        } else {
            goBack();
        }
    };

    const checkUnsavedChanges = () => {
        if (!originalFormData) {
            return (
                formData.first_name ||
                formData.last_name ||
                formData.email_address ||
                formData.relationship_id ||
                formData.access_benefits ||
                formData.access_portals ||
                formData.access_private_assets ||
                formData.access_sso
            );
        }

        return JSON.stringify(formData) !== JSON.stringify(originalFormData);
    };

    const isInviteButtonDisabled = () => {
        if (!invitationExpiresAt) return false;

        const expirationDate = new Date(invitationExpiresAt);
        const now = new Date();
        const hoursSinceExpiration = (now - expirationDate) / (1000 * 60 * 60);

        return hoursSinceExpiration < 24;
    };

    const formatExpirationDate = () => {
        if (!invitationExpiresAt) return '';

        try {
            const date = new Date(invitationExpiresAt);
            return format(date, 'MMM dd yyyy h:mm a');
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    const getFieldClasses = fieldName => {
        return `form-control ${errors[fieldName] ? 'is-invalid' : ''}`;
    };

    if (isLoading) {
        const spinner = (
            <div className="text-center mt-5">
                <Spinner width={50} className={styles.loadingSpinner} />
            </div>
        );

        if (isMobilePage) {
            return spinner;
        }

        return (
            <div
                className={
                    isModal ? styles.modalLoadingContainer : 'page-content'
                }
            >
                <div className="card">
                    <div className="card-body px-0 py-4">{spinner}</div>
                </div>
            </div>
        );
    }

    const formContent = (
        <>
            {isMobilePage && (
                <div className={styles.mobilePageNav}>
                    <button
                        type="button"
                        className={styles.mobileBackBtn}
                        onClick={goBack}
                    >
                        <BackNavIcon />
                    </button>
                    {isEditMode
                        ? `${formData.first_name} ${formData.last_name}`
                        : 'New Additional User'}
                </div>
            )}
            <div
                className={`justify-content-between w-100 ${isMobilePage ? 'd-none' : 'd-flex'}`}
            >
                <h2 className="page-title float-start ms-4 m-0 col-auto col-lg-2 text-nowrap align-self-xl-baseline">
                    {isEditMode
                        ? `${formData.first_name} ${formData.last_name}`
                        : 'New Additional User'}
                </h2>
                <div
                    className="col-auto col-lg-2 d-flex me-3 align-self-xl-baseline justify-content-end"
                    style={{ width: 'fit-content' }}
                >
                    <div className="float-end d-flex align-items-center align-self-baseline">
                        {isEditMode && (
                            <span
                                type="button"
                                onClick={openDeleteConfirmationModal}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-trash3 me-2 text-danger" />
                                {getText('msg_0a0a90f6c5a9')}
                            </span>
                        )}
                        <span
                            type="button"
                            onClick={handleClose}
                            className="ing-text-buttons mx-3"
                        >
                            <i className="bi bi-x me-2" />
                            {getText('msg_bbfa773e5a63')}
                        </span>
                    </div>
                </div>
            </div>

            <div className="col-12 col-xs-12 col-md-12 col-lg-10 m-auto pb-5 pt-5 px-3 px-lg-0">
                <div className="mb-4">
                    <h5
                        className={`d-inline-block mb-0 ${styles.sectionHeader}`}
                    >
                        {getText('msg_1aec6cffe9f1')}
                    </h5>
                    {/* <hr
                        className="d-none d-md-inline-block ms-3 mb-0"
                        style={{
                            width: 'calc(100% - 180px)',
                            verticalAlign: 'middle',
                        }}
                    /> */}
                </div>

                <div className="row my-3">
                    <div className="col-12 col-md-5 pe-md-2 mb-3">
                        <label htmlFor="first_name">
                            {getText('msg_b6ea992aab46')}{' '}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            id="first_name"
                            name="first_name"
                            className={getFieldClasses('first_name')}
                            value={formData.first_name}
                            onChange={handleInputChange}
                            placeholder={getText('msg_5bf521d8d975')}
                            maxLength={50}
                        />
                        {errors.first_name && (
                            <div className="invalid-feedback">
                                {errors.first_name}
                            </div>
                        )}
                    </div>

                    <div className="col-12 col-md-2 px-md-2 mb-3">
                        <label htmlFor="middle_name">
                            {getText('msg_ced02810fc9e')}
                        </label>
                        <input
                            type="text"
                            id="middle_name"
                            name="middle_name"
                            className="form-control"
                            value={formData.middle_name}
                            onChange={handleInputChange}
                            placeholder=""
                            maxLength={1}
                        />
                    </div>

                    <div className="col-12 col-md-5 ps-md-2 mb-3">
                        <label htmlFor="last_name">
                            {getText('msg_863cb39fbe7d')}{' '}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            id="last_name"
                            name="last_name"
                            className={getFieldClasses('last_name')}
                            value={formData.last_name}
                            onChange={handleInputChange}
                            placeholder={getText('msg_d05ae9c49909')}
                            maxLength={50}
                        />
                        {errors.last_name && (
                            <div className="invalid-feedback">
                                {errors.last_name}
                            </div>
                        )}
                    </div>
                </div>

                <div className="row my-3">
                    <div className="col-12 col-md-6 pe-md-2 mb-3">
                        <label htmlFor="email_address">
                            {getText('msg_84add5b29527')}{' '}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            type="email"
                            id="email_address"
                            name="email_address"
                            className={getFieldClasses('email_address')}
                            value={formData.email_address}
                            onChange={handleInputChange}
                            placeholder={getText('msg_122a389dbc8b')}
                            maxLength={100}
                        />
                        {errors.email_address && (
                            <div className="invalid-feedback">
                                {errors.email_address}
                            </div>
                        )}
                    </div>

                    <div className="col-12 col-md-6 ps-md-2 mb-3">
                        <label htmlFor="relationship_id">
                            {getText('msg_9b4a86cba424')}{' '}
                            <span className="text-danger">*</span>
                        </label>
                        <Dropdown
                            name="relationship_id"
                            defaultValue={
                                relationshipOptions.find(
                                    option =>
                                        option.value ===
                                        formData.relationship_id,
                                )?.label || 'Select relationship'
                            }
                            options={relationshipOptions}
                            onChange={value =>
                                handleDropdownChange('relationship_id', value)
                            }
                            value={formData.relationship_id || ''}
                            className={
                                errors.relationship_id ? 'is-invalid' : ''
                            }
                        />
                        {errors.relationship_id && (
                            <div className="invalid-feedback d-block">
                                {errors.relationship_id}
                            </div>
                        )}
                    </div>
                </div>

                <hr className="my-4" />
                <div className="mb-4 mt-3">
                    <h5 className={styles.sectionHeader}>
                        {getText('msg_2f81a22de0af')}{' '}
                        <span className="text-danger">*</span>
                        <Tooltip
                            html="User must have access to benefits or portals in order to be saved and invited to the site."
                            placement="right"
                        >
                            <i
                                className="bi bi-info-circle"
                                style={{
                                    paddingLeft: '5px',
                                    fontSize: '13px',
                                    color: 'var(--iNGAGED-Primary-Color)',
                                }}
                            />
                        </Tooltip>
                    </h5>
                    <p className="text-muted">{getText('msg_c9071c09b6e9')}</p>
                </div>

                {showIndividualBenefits && (
                    <div className="row">
                        <div className={`${styles.accessCheckbox}`}>
                            <input
                                type="checkbox"
                                id="access_benefits"
                                name="access_benefits"
                                className="form-check-input"
                                checked={formData.access_benefits}
                                onChange={handleInputChange}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="access_benefits"
                            >
                                <strong>{getText('msg_927f40673764')}</strong>
                                <br />
                                <small className="text-muted">
                                    {getText('msg_95f4fb9d524d')}
                                </small>
                            </label>
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className={`${styles.accessCheckbox}`}>
                        <input
                            type="checkbox"
                            id="access_private_assets"
                            name="access_private_assets"
                            className="form-check-input"
                            checked={formData.access_private_assets}
                            onChange={handleInputChange}
                        />
                        <label
                            className="form-check-label"
                            htmlFor="access_private_assets"
                        >
                            <strong>{getText('msg_71f8a28d0126')}</strong>
                            <br />
                            <small className="text-muted">
                                {getText('msg_7e8624fe9cb9')}
                            </small>
                        </label>
                    </div>
                </div>
                <div className="row">
                    <div className={`${styles.accessCheckbox}`}>
                        <input
                            type="checkbox"
                            id="access_portals"
                            name="access_portals"
                            className="form-check-input"
                            checked={formData.access_portals}
                            onChange={handleInputChange}
                        />
                        <label
                            className="form-check-label"
                            htmlFor="access_portals"
                        >
                            <strong>{getText('msg_a0c88242e813')}</strong>
                            <br />
                            <small className="text-muted">
                                {getText('msg_91e6c404ce13')}
                            </small>
                        </label>
                    </div>
                </div>
                {clientTierMeetsRequirement(TenantTiers.Bronze) && (
                    <div className="row">
                        <div className={`${styles.accessCheckbox}`}>
                            <input
                                type="checkbox"
                                id="access_sso"
                                name="access_sso"
                                className="form-check-input"
                                checked={formData.access_sso}
                                onChange={handleInputChange}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="access_sso"
                            >
                                <strong>{getText('msg_8fc091ee088f')}</strong>
                                <br />
                                <small className="text-muted">
                                    {getText('msg_f29c7b08a87e')}
                                </small>
                            </label>
                        </div>
                    </div>
                )}

                <div className="mb-4 mt-5">
                    <hr className="my-4" />
                    <h5 className={styles.sectionHeader}>
                        {getText('msg_b233e775d63b')}
                    </h5>
                    <p className="text-muted">{getText('msg_62d91e60af2b')}</p>
                    <div className="d-flex justify-content-end me-6 mt-4">
                        <div
                            className={`d-flex flex-column gap-3 ${styles.inviteSection}`}
                        >
                            <button
                                type="button"
                                className="button-base ing-button-primary-left m-auto"
                                onClick={handleSendInvite}
                                disabled={
                                    isSendingInvite || isInviteButtonDisabled()
                                }
                            >
                                {isSendingInvite ? 'Sending...' : 'Send Invite'}
                            </button>
                            {invitationExpiresAt && (
                                <>
                                    <p
                                        className="text-muted fst-italic text-end m-auto"
                                        style={{ fontSize: '14px' }}
                                    >
                                        {getText('msg_5e71e4fbe4fb')}{' '}
                                        {formatExpirationDate()}
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div
                    className={`mt-5 pt-4 ${isMobilePage ? 'd-flex flex-column gap-3' : 'd-flex justify-content-between'}`}
                >
                    {isMobilePage ? (
                        <>
                            <button
                                type="button"
                                className="button-base ing-button-primary-left w-100"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                type="button"
                                className="ing-popup-blue-negative w-100"
                                onClick={handleClose}
                            >
                                {getText('msg_77dfd2135f4d')}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="ing-popup-blue-negative"
                                onClick={handleClose}
                            >
                                {getText('msg_77dfd2135f4d')}
                            </button>
                            <button
                                type="button"
                                className="button-base ing-button-primary-left"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>
    );

    if (isModal) {
        return formContent;
    }

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">{formContent}</div>
            </div>
        </div>
    );
};

AdditionalUserForm.propTypes = {
    userId: PropTypes.string,
    onClose: PropTypes.func,
    onSuccess: PropTypes.func,
    isModal: PropTypes.bool,
};

export default AdditionalUserForm;
